/* =============================================================================
 * Icones.
 *
 * O app inteiro usava emoji como icone. Emoji nao aceita cor, muda de desenho
 * a cada sistema operacional (o 🛡️ do Android nao e' o do iOS) e alinha mal na
 * linha de base. Aqui e' um traco de 24px, currentColor, alinhado.
 *
 * Nome desconhecido devolve null de proposito: `anexar` em dom.js ignora filho
 * falsy, entao um erro de digitacao degrada para nada em vez de estourar a
 * tela inteira.
 *
 * Compativel com o que ja' existe sem nenhuma mudanca: estadoVazio faz
 * h('div', {...}, icone) e `anexar` ja' trata Node - entao
 * estadoVazio({ icone: icone('calendario', { tamanho: 32 }) }) funciona.
 * A excecao e' botao-favorito.js, que usava textContent (nao segura SVG).
 * ========================================================================== */

import { svg } from '@shared/ui/dom.js';

/**
 * Cada icone e' uma lista de formas. String vira <path d="...">; objeto vira a
 * tag que ele pedir, com o resto virando atributo - e' assim que os poucos
 * icones com area preenchida convivem com o traco padrao.
 */
const FORMAS = {
    //As "pernas" abertas de um trofeu desenhado com hastes diagonais viram uma
    //ampulheta em 22px. Haste reta + base solida le' como trofeu em qualquer
    //tamanho.
    trofeu: ['M7 3h10v5.5a5 5 0 0 1-10 0z', 'M7 4.5H4.5a2.5 2.5 0 0 0 0 5H7', 'M17 4.5h2.5a2.5 2.5 0 0 1 0 5H17', 'M12 14v4', 'M8 21h8'],

    'escudo-clube': ['M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z'],

    estrela: ['M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.6-5.9-3.1-5.9 3.1 1.2-6.6L2.5 9.4l6.6-.9z'],

    'estrela-cheia': [{ t: 'path', d: 'M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.6-5.9-3.1-5.9 3.1 1.2-6.6L2.5 9.4l6.6-.9z', fill: 'currentColor' }],

    pessoa: [{ t: 'circle', cx: '12', cy: '8', r: '4' }, 'M20 21v-1a6 6 0 0 0-6-6h-4a6 6 0 0 0-6 6v1'],

    casa: ['M3 10.2a2 2 0 0 1 .7-1.5l7-6a2 2 0 0 1 2.6 0l7 6a2 2 0 0 1 .7 1.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z', 'M9.5 21v-7h5v7'],

    tabela: [{ t: 'rect', x: '3', y: '4', width: '18', height: '16', rx: '2' }, 'M3 9.5h18', 'M3 15h18', 'M8.5 4v16'],

    //Barras crescendo sobre um eixo. 'tabela' nao servia para Estatisticas: a
    //grade dela le como CLASSIFICACAO, que e' outra tela - e no menu do time as
    //duas ficam lado a lado.
    grafico: ['M4 20V4', 'M4 20h16', 'M8.5 20v-5', 'M13 20V9.5', 'M17.5 20v-8'],

    calendario: [{ t: 'rect', x: '3', y: '5', width: '18', height: '16', rx: '2' }, 'M3 10h18', 'M8 3v4', 'M16 3v4'],

    //O mesmo calendario com os DIAS dentro. Existe porque 'calendario' ja' e' de
    //Rodadas na competicao, e duas abas vizinhas com o mesmo desenho nao se
    //distinguem de relance - que e' justamente o trabalho do icone na fileira
    //que rola no telefone. Os pontos sao o mes preenchido, nao decoracao.
    'calendario-mes': [{ t: 'rect', x: '3', y: '5', width: '18', height: '16', rx: '2' }, 'M3 10h18', 'M8 3v4', 'M16 3v4', 'M7.5 13.5h.01', 'M12 13.5h.01', 'M16.5 13.5h.01', 'M7.5 17.5h.01', 'M12 17.5h.01'],

    documento: ['M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z', 'M14 2v5h5', 'M9 13h6', 'M9 17h4'],

    //Seta COM haste, e nao o chevron: os dois convivem na mesma tela e dizem
    //coisas diferentes. O chevron e' "o proximo item desta lista"; a seta
    //inteira e' o voltar/avancar da casca, o mesmo desenho que o navegador usa
    //na barra dele - e' dai' que a pessoa reconhece o par de relance.
    'seta-esquerda': ['M20 12H4', 'M10.5 5.5L4 12l6.5 6.5'],

    'seta-direita': ['M4 12h16', 'M13.5 5.5L20 12l-6.5 6.5'],

    'chevron-esquerda': ['M15 18l-6-6 6-6'],

    'chevron-direita': ['M9 18l6-6-6-6'],

    'chevron-baixo': ['M6 9.5l6 6 6-6'],

    'chevron-cima': ['M6 14.5l6-6 6 6'],

    busca: [{ t: 'circle', cx: '11', cy: '11', r: '7' }, 'M21 21l-4.3-4.3'],

    check: ['M20 6.5L9.5 17 4 11.5'],

    'sol-lua': [
        { t: 'circle', cx: '12', cy: '12', r: '9' },
        { t: 'path', d: 'M12 3a9 9 0 0 1 0 18z', fill: 'currentColor', stroke: 'none' },
    ],

    alerta: ['M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z', 'M12 9v4.5', 'M12 17.5h.01'],

    bola: [{ t: 'circle', cx: '12', cy: '12', r: '9.5' }, 'M12 7.6l3.7 2.7-1.4 4.4H9.7l-1.4-4.4z', 'M12 2.5v5.1', 'M2.9 9.3l5.4 1', 'M21.1 9.3l-5.4 1', 'M6.6 20.2l3.1-5.5', 'M17.4 20.2l-3.1-5.5'],

    filtro: ['M21.5 3.5h-19l7.6 9v6.6l3.8 1.9v-8.5z'],

    pessoas: [{ t: 'circle', cx: '9', cy: '8', r: '3.5' }, 'M16 21v-1a5 5 0 0 0-5-5H7a5 5 0 0 0-5 5v1', 'M16.5 4.6a3.5 3.5 0 0 1 0 6.8', 'M18 15.2a5 5 0 0 1 4 4.8v1'],

    camisa: ['M8 3l4 2 4-2 5 3-2.5 4L17 9v12H7V9l-1.5 1L3 6z'],

    prancheta: [{ t: 'rect', x: '4', y: '4', width: '16', height: '17', rx: '2' }, 'M9 2.5h6v3H9z', 'M8.5 11h7', 'M8.5 15.5h4.5'],

    relogio: [{ t: 'circle', cx: '12', cy: '12', r: '9' }, 'M12 6.5V12l3.5 2.5'],

    //Area preenchida de proposito: o cartao e' reconhecido pela mancha de cor,
    //nao pelo contorno - vazado ele viraria "um retangulo torto" em 16px.
    cartao: [{ t: 'rect', x: '7.5', y: '3', width: '9', height: '18', rx: '1.5', transform: 'rotate(14 12 12)', fill: 'currentColor', stroke: 'none' }],

    apito: [{ t: 'circle', cx: '8.5', cy: '13.5', r: '5.5' }, 'M13.9 11.3h7.1l-1.7-3.6h-6.6'],

    local: ['M12 21.5s7-6 7-11.5a7 7 0 1 0-14 0c0 5.5 7 11.5 7 11.5z', { t: 'circle', cx: '12', cy: '10', r: '2.5' }],

    trocar: ['M4 8h15', 'M15.5 4.5L19 8l-3.5 3.5', 'M20 16H5', 'M8.5 12.5L5 16l3.5 3.5'],

    //Lapis com a ponta virada para baixo-esquerda: a mesma leitura de "editar"
    //do resto do Prisma, sem o quadrado com lapis (que em 16px vira borrao).
    lapis: ['M4 20.5h4.2L20 8.7a2.6 2.6 0 0 0-3.7-3.7L4.5 16.8z', 'M14.5 6.8l3.7 3.7'],

    //Cruz simples, sem circulo em volta: em 16px o circulo vira uma mancha e o
    //"mais" some dentro dele.
    mais: ['M12 5v14', 'M5 12h14'],

    //O par do `mais`, e nao o caractere '−': dois botoes lado a lado precisam do
    //mesmo traco e da mesma caixa de 24px, e sinal tipografico ao lado de icone
    //desenhado sai com peso e altura diferentes.
    menos: ['M5 12h14'],

    'enviar-saida': ['M12 3v12', 'M7.5 7.5L12 3l4.5 4.5', 'M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4'],

    'receber-entrada': ['M12 15V3', 'M7.5 10.5L12 15l4.5-4.5', 'M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4'],

    //Envelope com a aba em V - a leitura de "e-mail" que sobrevive a 14px, que
    //e' o tamanho do chip do hero.
    email: [{ t: 'rect', x: '3', y: '5', width: '18', height: '14', rx: '2' }, 'M3.5 7l8.5 6 8.5-6'],

    //Monofone na diagonal - a silhueta de "telefone" que continua legivel ao lado
    //do nome de um contato, no mesmo tamanho do icone de e-mail.
    telefone: ['M8.4 3.5L10.6 8l-2 2a13 13 0 0 0 5.4 5.4l2-2 4.5 2.2v3.1a1.8 1.8 0 0 1-2 1.8C10.2 19.8 4.2 13.8 3.2 5.5a1.8 1.8 0 0 1 1.8-2h3.4z'],

    //Corpo com o resalto do visor em cima e a lente no meio. Ele vive num selo de
    //13px sobre o retrato, entao nao carrega o significado sozinho - o botao tem
    //aria-label; aqui o desenho so' precisa dizer "foto" de relance.
    camera: ['M3 9.5a2.5 2.5 0 0 1 2.5-2.5h2.3l1.3-2.2h5.8L16.2 7h2.3A2.5 2.5 0 0 1 21 9.5v8a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 17.5z', { t: 'circle', cx: '12', cy: '13.2', r: '3.4' }],

    //O X de fechar. Duas diagonais soltas, sem circulo em volta: o botao ja' tem
    //area propria, e o anel so' engordaria o desenho dentro dela.
    fechar: ['M6.5 6.5l11 11', 'M17.5 6.5l-11 11'],

    //O par de "mostrar/ocultar senha". Sao dois desenhos, e nao um so' com uma
    //barra por cima, porque o gatilho troca de icone A CADA toque: a lente
    //inteira e a lente riscada precisam ter o mesmo peso de traco e a mesma
    //caixa, senao o botao "pula" quando o estado muda.
    olho: ['M2.5 12S6.4 5.2 12 5.2 21.5 12 21.5 12 17.6 18.8 12 18.8 2.5 12 2.5 12z', { t: 'circle', cx: '12', cy: '12', r: '3.2' }],

    'olho-cortado': ['M4 4l16 16', 'M9.6 5.6A9.8 9.8 0 0 1 12 5.2c5.6 0 9.5 6.8 9.5 6.8a18.6 18.6 0 0 1-3.8 4.5', 'M6.4 7.9A18.3 18.3 0 0 0 2.5 12S6.4 18.8 12 18.8a9.6 9.6 0 0 0 3.5-.7', 'M9.8 9.9a3.1 3.1 0 0 0 4.4 4.3'],

    //A lupa do `busca` com o sinal dentro - e nao o `mais`/`menos` sozinho, que
    //ao lado de um botao de girar leria como "adicionar" e "remover" alguma
    //coisa. O par de zoom precisa dizer AMPLIAR, e quem diz isso e' a lente.
    'zoom-mais': [{ t: 'circle', cx: '11', cy: '11', r: '7' }, 'M21 21l-4.3-4.3', 'M11 8.2v5.6', 'M8.2 11h5.6'],

    'zoom-menos': [{ t: 'circle', cx: '11', cy: '11', r: '7' }, 'M21 21l-4.3-4.3', 'M8.2 11h5.6'],

    //Seta circular no sentido do relogio, e o par dela espelhado. Os dois giros
    //existem porque a foto de documento chega deitada para os DOIS lados, e
    //chegar ao outro sentido com tres toques no mesmo botao e' o tipo de coisa
    //que se faz uma vez e nao se faz de novo.
    'girar-direita': ['M20 12a8 8 0 1 1-2.4-5.7', 'M20.5 4v4.2h-4.2'],

    'girar-esquerda': ['M4 12a8 8 0 1 0 2.4-5.7', 'M3.5 4v4.2h4.2'],

    //O aparelho de pe', com o alto-falante em cima e a barra de gestos embaixo.
    //E' o icone de "instalar o app": o assunto ali nao e' baixar um arquivo (o
    //`receber-entrada` ja' e' do offline, e as duas telas vivem lado a lado no
    //perfil) - e' o Futigol virando um icone NESTE aparelho.
    celular: [{ t: 'rect', x: '6', y: '2.5', width: '12', height: '19', rx: '2.5' }, 'M10.5 5.5h3', 'M10 18.3h4'],

    //Os quatro cantos de um enquadramento: "encaixar na tela de novo". E' o
    //desfazer do zoom e do giro, e por isso nao usa seta - nada volta ao lugar
    //anterior, tudo volta ao ponto de partida.
    ajustar: ['M9 3.5H5.5a2 2 0 0 0-2 2V9', 'M15 3.5h3.5a2 2 0 0 1 2 2V9', 'M20.5 15v3.5a2 2 0 0 1-2 2H15', 'M3.5 15v3.5a2 2 0 0 0 2 2H9'],

    //O par de janela, na leitura que todo sistema operacional usa: um retangulo
    //para ocupar a tela, dois sobrepostos para voltar ao tamanho anterior. Nao
    //confundir com `ajustar`, que e' o enquadramento da IMAGEM dentro da janela.
    maximizar: [{ t: 'rect', x: '4', y: '4.5', width: '16', height: '15', rx: '2' }],

    restaurar: [{ t: 'rect', x: '8', y: '8.5', width: '12', height: '11', rx: '2' }, 'M16 5.5H6a2 2 0 0 0-2 2v9'],
};

/**
 * @param {string} nome    chave de FORMAS
 * @param {number} tamanho lado em px (o viewBox e' sempre 24)
 * @param {string} titulo  passe um texto quando o icone for a UNICA pista do
 *                         significado; por omissao e' decorativo
 * @returns {SVGElement|null} null quando o nome nao existe
 */
export function icone(nome, { tamanho = 20, titulo = null, classe = '' } = {}) {
    const formas = FORMAS[nome];
    if (!formas) return null;

    return svg(
        'svg',
        {
            class: `icone${classe ? ' ' + classe : ''}`,
            viewBox: '0 0 24 24',
            width: String(tamanho),
            height: String(tamanho),
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '1.75',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round',
            role: titulo ? 'img' : null,
            'aria-hidden': titulo ? null : 'true',
            //Sem isto o IE/Edge legado punha o SVG na ordem de tabulacao.
            focusable: 'false',
        },
        [titulo && svg('title', {}, titulo), ...formas.map((forma) => (typeof forma === 'string' ? svg('path', { d: forma }) : svg(forma.t, { ...forma, t: null })))]
    );
}
